// i18n/ru.ts — русский словарь «Упряжка» (default locale).
// KISS: плоский namespace `common`, `auth`, `nav`, `tasks`, `crm`, `calendar`, `dashboard`.
// Каждое значение — строка, готовая к подстановке через {placeholder}.
//
// Использование:
//   import { t } from '@/i18n';
//   t('auth.login.title')                          // 'Вход в Упряжку'
//   t('common.greeting', { name: 'Иван' })         // 'Привет, Иван!'

export type Dict = Record<string, string>;

/** Корневой словарь ru-RU. */
export const ru: Dict = {
  // common
  'common.appName': 'Упряжка',
  'common.appSubtitle': 'Корпоративный портал',
  'common.greeting': 'Привет, {name}!',
  'common.welcome': 'Добро пожаловать',
  'common.save': 'Сохранить',
  'common.cancel': 'Отмена',
  'common.delete': 'Удалить',
  'common.edit': 'Редактировать',
  'common.create': 'Создать',
  'common.search': 'Поиск',
  'common.loading': 'Загрузка…',
  'common.error': 'Ошибка',
  'common.retry': 'Повторить',
  'common.back': 'Назад',
  // B2 (pintest-fix-B): fallback для неизвестного id сущности вместо обрезка UUID.
  'common.unknown': 'Неизвестно',
  // U-20 (sled-V9 W6): подсказка поиска департамента (DepartmentAutocomplete).
  'common.departmentSearchHint': 'Начните вводить название департамента',
  // F-2 (pintest V15): невалидная дата в полях MUI X (DateTimeField) — error-текст.
  'forms.dateInvalid': 'Некорректная дата',

  // auth
  'auth.login.title': 'Вход в Упряжку',
  'auth.login.subtitle': 'Введите учётные данные для входа в систему',
  'auth.login.username': 'Имя пользователя',
  'auth.login.password': 'Пароль',
  'auth.login.submit': 'Войти',
  'auth.login.error.invalid': 'Неверные учётные данные',
  'auth.logout': 'Выйти',
  'auth.userMenu.label': 'Меню пользователя',

  // nav
  'nav.dashboard': 'Дашборд',
  'nav.tasks': 'Задачи',
  'nav.deals': 'Сделки',
  'nav.contacts': 'Контакты',
  'nav.companies': 'Компании',
  'nav.calendar': 'Календарь',
  'nav.admin': 'Администрирование',

  // user menu (R39b / R40; V16 W11 — админ-справочники)
  'menu.profile': 'Профиль',
  'menu.team': 'Команда',
  'menu.admin': 'Администрирование',

  // topbar (R42 / U-13: user menu, bell-заглушка, account tooltip)
  'topbar.account.tooltip': 'Аккаунт: {name}',
  'topbar.notifications.label': 'Уведомления',
  'topbar.notifications.tooltip': 'Уведомления — скоро',

  // dashboard
  'dashboard.subtitle': 'Сводка по системе Упряжка',
  'dashboard.kpi.tasks': 'Задачи',
  'dashboard.kpi.deals': 'Сделки',
  'dashboard.kpi.eventsToday': 'События сегодня',
  'dashboard.kpi.contacts': 'Контакты',
  'dashboard.recentActivity': 'Последняя активность',
  'dashboard.adminNote': 'Вы вошли как администратор. Доступны audit-events API и расширенные действия.',
  // U-25 (sled-V9): SectionHeader виджета Dashboard — заголовки и action.
  'dashboard.auditLogTitle': 'Журнал аудита',
  'dashboard.auditLogAll': 'Все события',
  'dashboard.recentActivityTitle': 'Последние действия',

  // tasks
  'tasks.title': 'Задачи',
  'tasks.subtitle': 'Управление задачами вашего отдела',
  'tasks.create': 'Создать задачу',
  'tasks.empty': 'Нет задач',
  'tasks.status.NEW': 'Новая',
  'tasks.status.IN_PROGRESS': 'В работе',
  'tasks.status.BLOCKED': 'Заблокирована',
  'tasks.status.DONE': 'Выполнена',
  'tasks.status.CANCELLED': 'Отменена',
  'tasks.status.noTransitions': 'Нет доступных переходов для этого статуса',
  'tasks.operationError': 'Не удалось выполнить операцию',
  // R41 / U-16: приоритеты задачи (backend TaskPriority).
  'tasks.priority.LOW': 'Низкий',
  'tasks.priority.MEDIUM': 'Средний',
  'tasks.priority.HIGH': 'Высокий',
  'tasks.priority.CRITICAL': 'Критический',
  // R38b / U-9: связь задача ↔ сделка
  'tasks.deal': 'Сделка',
  'tasks.linkedDeal': 'Связанная сделка',
  // FIX-C (pintest S4): связанная сделка удалена/отменена (backend → NotFound).
  'tasks.dealDeleted': 'Сделка удалена',
  // B1 (pintest-fix-B): обязательный департамент в форме создания задачи.
  'tasks.department': 'Департамент',
  'tasks.departmentRequired': 'Выберите департамент',
  // U-21 (sled-V9): срок задачи — не в прошлом (zod-рефайн, ISO Instant).
  'tasks.dueAtPast': 'Срок не может быть в прошлом',
  // U-23 (sled-V9): сортируемые колонки списка задач.
  'tasks.list.createdAt': 'Создана',
  'tasks.list.dueAt': 'Срок',

  // crm
  'crm.deals.title': 'Сделки',
  'crm.deals.subtitle': 'Управление сделками CRM',
  'crm.deals.create': 'Создать сделку',
  'crm.deals.department': 'Департамент',
  'crm.deals.department.required': 'Выберите департамент',
  // U-17 (sled-V9): валидация формы сделки — дата/сумма
  'crm.deals.dueDatePast': 'Дата не может быть в прошлом',
  'crm.deals.amountInvalid': 'Укажите сумму числом',
  'crm.deals.amountMin': 'Сумма должна быть ≥ 0',
  // U-32 (sled-V11): сумма сделки — пусто/<=0 — отдельные сообщения (amountSchema в lib/money.ts).
  'crm.deals.amountRequired': 'Укажите сумму',
  'crm.deals.amountPositive': 'Сумма должна быть больше 0',
  'crm.deals.createError': 'Не удалось создать сделку',
  'crm.deals.edit.title': 'Редактирование сделки',
  'crm.deals.edit.conflict': 'Данные изменились другим пользователем. Обновите страницу и повторите.',
  // U-28 (sled-V10 W2): суффикс опции «назначенный контакт» в диалоге редактирования сделки.
  'crm.deals.edit.assignedContactSuffix': 'назначен',
  'crm.deals.editError': 'Не удалось сохранить сделку',
  'crm.deals.nextContact': 'Назначить следующий контакт',
  // U-18 (sled-V9): окно контактов в карточке сделки — подписи блоков.
  'crm.deals.nextContactLabel': 'Следующий контакт',
  'crm.deals.lastContactLabel': 'Последний контакт',

  // dealDateField (U-27 sled-V10): сегменты тумблера поля дат-диапазона в фильтре сделок.
  'dealDateField.createdAt': 'Создана',
  'dealDateField.nextContactAt': 'Следующий контакт',
  'dealDateField.dueDate': 'Плановая дата закрытия',
  // R41 / U-16: стадии сделки (backend DealStage) — конвенция <domain>.<field>.<VALUE>.
  'crm.deals.stage.NEW': 'Новая',
  'crm.deals.stage.QUALIFIED': 'Квалифицирована',
  'crm.deals.stage.NEGOTIATION': 'Переговоры',
  'crm.deals.stage.PROPOSAL': 'Предложение',
  'crm.deals.stage.WON': 'Выиграна',
  'crm.deals.stage.LOST': 'Проиграна',
  // U-19 (sled-V9 W5): DealStagePath — aria-подпись кнопки стадии пути.
  'crm.deals.stagePath.aria': 'Стадия {stage}',
  // V16 W3: диалог причины отказа при переходе сделки в LOST.
  'crm.deals.lostReason.title': 'Причина отказа',
  'crm.deals.lostReason.label': 'Причина отказа',
  'crm.deals.lostReason.confirm': 'Подтвердить',
  'crm.deals.lostReason.loadError': 'Не удалось загрузить причины отказа',
  'crm.deals.rejectionReason': 'Причина отказа',
  // R38b / U-9: таб «Задачи» на карточке сделки
  'crm.deals.tasksTab': 'Задачи',
  'crm.deals.createTask': 'Создать задачу',
  'crm.deals.noTasks': 'Задач, связанных со сделкой, нет',
  'crm.contacts.title': 'Контакты',
  'crm.companies.title': 'Компании',
  // U-23 (sled-V9): сортируемая колонка «Создана» в списках компаний/контактов.
  'crm.companies.list.createdAt': 'Создана',
  'crm.contacts.list.createdAt': 'Создана',
  // U-22 (sled-V9): мягкая валидация сайта — схема необязательна, TLD обязателен.
  'crm.companies.websiteHint': 'Можно без https:// — подставим',
  'crm.companies.websiteInvalid': 'Сайт должен быть доменом или URL (схема необязательна)',

  // calendar
  'calendar.title': 'Календарь',
  'calendar.subtitle': 'Календарь событий',
  'calendar.create': 'Создать событие',
  'calendar.empty': 'Нет событий в выбранном диапазоне',
  'calendar.view.month': 'Месяц',
  'calendar.view.week': 'Неделя',
  'calendar.view.day': 'День',
  // B1 (pintest-fix-B): обязательный департамент в форме создания события.
  'calendar.department': 'Департамент',
  'calendar.departmentRequired': 'Выберите департамент',
  // U-30 (sled-V10 W1): реальная причина ошибки + cross-field endAt > startAt.
  'calendar.createError': 'Ошибка создания события',
  'calendar.endAtAfterStart': 'Дата окончания должна быть позже даты начала',
  // R41 / U-16: типы событий календаря (backend CalendarType).
  'calendar.type.MEETING': 'Встреча',
  'calendar.type.CALL': 'Звонок',
  'calendar.type.TASK': 'Задача',
  'calendar.type.REMINDER': 'Напоминание',
  // R41 / U-16: статусы участника события (backend AttendeeStatus).
  'calendar.attendee.PENDING': 'Ожидает ответа',
  'calendar.attendee.ACCEPTED': 'Принято',
  'calendar.attendee.DECLINED': 'Отклонено',
  'calendar.attendee.TENTATIVE': 'Предварительно',

  // R41 / U-16: типы событий аудита (backend AuditEventType, 46 значений).
  'audit.eventType.USER_CREATED': 'Создан пользователь',
  'audit.eventType.USER_UPDATED': 'Обновлён пользователь',
  'audit.eventType.USER_DELETED': 'Пользователь удалён',
  'audit.eventType.USER_LOGIN_SUCCESS': 'Вход выполнен',
  'audit.eventType.USER_LOGIN_FAILED': 'Неудачный вход',
  'audit.eventType.USER_LOGOUT': 'Выход из системы',
  'audit.eventType.USER_PASSWORD_CHANGED': 'Пароль изменён',
  'audit.eventType.ROLE_ASSIGNED': 'Роль назначена',
  'audit.eventType.ROLE_UNASSIGNED': 'Роль снята',
  'audit.eventType.DEPARTMENT_CREATED': 'Создан департамент',
  'audit.eventType.DEPARTMENT_UPDATED': 'Департамент обновлён',
  'audit.eventType.DEPARTMENT_DELETED': 'Департамент удалён',
  'audit.eventType.USER_DEPARTMENT_ASSIGNED': 'Пользователь добавлен в департамент',
  'audit.eventType.USER_DEPARTMENT_UPDATED': 'Департамент пользователя обновлён',
  'audit.eventType.USER_DEPARTMENT_REMOVED': 'Пользователь исключён из департамента',
  'audit.eventType.TOKEN_REFRESHED': 'Токен обновлён',
  'audit.eventType.TOKEN_REFRESH_FAILED': 'Обновление токена не удалось',
  'audit.eventType.TASK_CREATED': 'Задача создана',
  'audit.eventType.TASK_UPDATED': 'Задача обновлена',
  'audit.eventType.TASK_DELETED': 'Задача удалена',
  'audit.eventType.TASK_ASSIGNED': 'Задача назначена исполнителю',
  'audit.eventType.TASK_UNASSIGNED': 'Назначение задачи снято',
  'audit.eventType.TASK_STATUS_CHANGED': 'Статус задачи изменён',
  'audit.eventType.TASK_ACCESS_DENIED_CROSS_DEPARTMENT': 'Доступ к задаче запрещён (кросс-департамент)',
  'audit.eventType.DEAL_CREATED': 'Сделка создана',
  'audit.eventType.DEAL_UPDATED': 'Сделка обновлена',
  'audit.eventType.DEAL_DELETED': 'Сделка удалена',
  'audit.eventType.DEAL_STAGE_CHANGED': 'Стадия сделки изменена',
  'audit.eventType.DEAL_ASSIGNED': 'Владелец сделки изменён',
  'audit.eventType.DEAL_COMMENT_ADDED': 'Добавлен комментарий к сделке',
  'audit.eventType.DEAL_ACCESS_DENIED_CROSS_DEPARTMENT': 'Доступ к сделке запрещён (кросс-департамент)',
  'audit.eventType.CONTACT_CREATED': 'Контакт создан',
  'audit.eventType.CONTACT_UPDATED': 'Контакт обновлён',
  'audit.eventType.CONTACT_DELETED': 'Контакт удалён',
  'audit.eventType.CONTACT_ACCESS_DENIED_CROSS_DEPARTMENT': 'Доступ к контакту запрещён (кросс-департамент)',
  'audit.eventType.COMPANY_CREATED': 'Компания создана',
  'audit.eventType.COMPANY_UPDATED': 'Компания обновлена',
  'audit.eventType.COMPANY_DELETED': 'Компания удалена',
  'audit.eventType.COMPANY_ACCESS_DENIED_CROSS_DEPARTMENT': 'Доступ к компании запрещён (кросс-департамент)',
  'audit.eventType.CALENDAR_EVENT_CREATED': 'Событие создано',
  'audit.eventType.CALENDAR_EVENT_UPDATED': 'Событие обновлено',
  'audit.eventType.CALENDAR_EVENT_DELETED': 'Событие удалено',
  'audit.eventType.CALENDAR_EVENT_ATTENDEE_ADDED': 'Участник добавлен',
  'audit.eventType.CALENDAR_EVENT_ATTENDEE_REMOVED': 'Участник удалён',
  'audit.eventType.CALENDAR_EVENT_ATTENDEE_RESPONDED': 'Участник ответил на приглашение',
  'audit.eventType.CALENDAR_EVENT_ACCESS_DENIED_CROSS_DEPARTMENT': 'Доступ к событию запрещён (кросс-департамент)',
  // V12 U-34/U-35: события лидов и платежей (микро-фикс U-16 — локализация аудит-типа).
  'audit.eventType.LEAD_CREATED': 'Лид создан',
  'audit.eventType.LEAD_UPDATED': 'Лид обновлён',
  'audit.eventType.LEAD_STATUS_CHANGED': 'Статус лида изменён',
  'audit.eventType.PAYMENT_CREATED': 'Платёж создан',
  'audit.eventType.PAYMENT_UPDATED': 'Платёж обновлён',
  // V14 W6 (§3.2.1): события аудита счетов (INVOICE_CREATED/UPDATED/STATUS_CHANGED).
  'audit.eventType.INVOICE_CREATED': 'Счёт создан',
  'audit.eventType.INVOICE_UPDATED': 'Счёт обновлён',
  'audit.eventType.INVOICE_STATUS_CHANGED': 'Статус счёта изменён',
  // V15: событие аудита доставки письма (EMAIL_DELIVERY_CREATED).
  'audit.eventType.EMAIL_DELIVERY_CREATED': 'Письмо создано',
  // R41 / U-16: типы сущностей аудита (entityType с бэкенда — lowercase key).
  'audit.entity.USER': 'Пользователь',
  'audit.entity.DEPARTMENT': 'Департамент',
  'audit.entity.ROLE': 'Роль',
  'audit.entity.TASK': 'Задача',
  'audit.entity.DEAL': 'Сделка',
  'audit.entity.CONTACT': 'Контакт',
  'audit.entity.COMPANY': 'Компания',
  'audit.entity.CALENDAR_EVENT': 'Событие',
  'audit.entity.LEAD': 'Лид',
  'audit.entity.PAYMENT': 'Платёж',
  'audit.entity.INVOICE': 'Счёт',
  'audit.entity.EMAIL_DELIVERY': 'Письмо',

  // profile (R39b / U-10)
  'profile.title': 'Мой профиль',
  'profile.subtitle': 'Личные данные и контакты',
  'profile.username': 'Имя пользователя',
  'profile.email': 'Email',
  'profile.displayName': 'Отображаемое имя',
  'profile.phone': 'Телефон',
  'profile.position': 'Должность',
  'profile.description': 'О себе',
  'profile.saveSuccess': 'Профиль обновлён',
  'profile.saveError': 'Не удалось обновить профиль',

  // team (R40 / U-11)
  'team.title': 'Команда',
  'team.subtitle': 'Управление пользователями портала (доступно администратору)',
  'team.create': 'Создать пользователя',
  'team.createSubmit': 'Создать',
  'team.createTitle': 'Новый пользователь',
  'team.createError': 'Не удалось создать пользователя',
  'team.operationError': 'Не удалось изменить статус пользователя',
  'team.loadError': 'Не удалось загрузить пользователей',
  'team.loadErrorDesc': 'Проверьте соединение с сервером и повторите попытку',
  'team.empty': 'Нет пользователей',
  'team.emptyDesc': 'Создайте первого пользователя кнопкой выше',
  'team.columns.user': 'Пользователь',
  'team.columns.email': 'Email',
  'team.columns.phone': 'Телефон',
  'team.columns.position': 'Должность',
  'team.columns.roles': 'Роли',
  'team.columns.status': 'Статус',
  'team.columns.actions': 'Действия',
  'team.activate': 'Активировать',
  'team.deactivate': 'Деактивировать',
  'team.form.username': 'Имя пользователя',
  'team.form.email': 'Email',
  'team.form.password': 'Пароль',
  'team.form.displayName': 'Отображаемое имя',
  'users.status.active': 'Активен',
  'users.status.locked': 'Заблокирован',
  'users.status.disabled': 'Отключён',

  // dict (V16 W11): admin-справочники причин и валют
  'dict.title': 'Администрирование',
  'dict.subtitle': 'Справочники причин отказа и валют',
  'dict.tabs.reasons': 'Причины',
  'dict.tabs.currencies': 'Валюты',
  'dict.reasons.create': 'Добавить причину',
  'dict.reasons.createTitle': 'Новая причина',
  'dict.reasons.editTitle': 'Редактировать причину',
  'dict.reasons.name': 'Название',
  'dict.reasons.empty': 'Нет причин',
  'dict.reasons.emptyDesc': 'Добавьте первую причину кнопкой выше',
  'dict.reasons.loadError': 'Не удалось загрузить причины',
  'dict.reasons.loadErrorDesc': 'Проверьте соединение с сервером и повторите попытку',
  'dict.reasons.saveError': 'Не удалось сохранить причину',
  'dict.reasons.deleteError': 'Не удалось удалить причину',
  'dict.currencies.create': 'Добавить валюту',
  'dict.currencies.createTitle': 'Новая валюта',
  'dict.currencies.editTitle': 'Редактировать валюту',
  'dict.currencies.code': 'Код',
  'dict.currencies.symbol': 'Символ',
  'dict.currencies.empty': 'Нет валют',
  'dict.currencies.emptyDesc': 'Добавьте первую валюту кнопкой выше',
  'dict.currencies.loadError': 'Не удалось загрузить валюты',
  'dict.currencies.loadErrorDesc': 'Проверьте соединение с сервером и повторите попытку',
  'dict.currencies.saveError': 'Не удалось сохранить валюту',
  'dict.currencies.deleteError': 'Не удалось удалить валюту',
  'dict.columns.status': 'Статус',
  'dict.columns.actions': 'Действия',
  'dict.status.active': 'Активна',
  'dict.status.inactive': 'Неактивна',
  'dict.activate': 'Активировать',
  'dict.deactivate': 'Деактивировать',

  // search (R63 / U-34-D): панель поиска с флажками (SearchFieldBar).
  'search.fields.label': 'Поля',
  'search.reset': 'Сбросить',
  'search.placeholder': 'Поиск…',
  'search.field.name': 'Название',
  'search.field.website': 'Сайт',
  'search.field.site_name': 'Имя сайта',
  'search.field.email': 'Email',
  'search.field.phone': 'Телефон',
  'search.field.tax_id': 'ИНН',
  'search.field.license_number': 'Номер лицензии',
  'search.field.ip_address': 'IP адрес',
  'search.field.first_name': 'Имя',
  'search.field.last_name': 'Фамилия',
  'search.field.company_id': 'Компания',
  'search.field.company_name': 'Компания',
  // V13 U-38 (W4): поля поиска сделок (DealSearchField) — паритет RU/EN.
  'search.field.title': 'Название',
  'search.field.description': 'Описание',
  'search.field.stage': 'Стадия',
  'search.field.currency': 'Валюта',
  'search.field.contact_id': 'Контакт',
  'search.field.owner_id': 'Владелец',

  // leads (R62 / V12 U-34-E): страницы лидов.
  'leads.title': 'Лиды',
  'leads.subtitle': 'Входящие заявки и ручные лиды',
  'leads.loading': 'Загрузка лидов…',
  'leads.create': 'Создать лид',
  'leads.create.dialogTitle': 'Создать лид',
  'leads.create.submit': 'Создать',
  'leads.create.error': 'Не удалось создать лид',
  'leads.empty': 'Нет лидов',
  'leads.emptyDesc': 'Создайте первый лид',
  'leads.search': 'Поиск лидов',
  'leads.loadError': 'Не удалось загрузить лиды',
  'leads.detail.notFound': 'Лид не найден',
  'leads.edit.dialogTitle': 'Редактировать лид',
  'leads.edit.error': 'Не удалось сохранить лид',
  // V16 W5: диалог причины отказа при переходе лида в DISCARDED.
  'leads.discardReason.title': 'Причина отказа',
  'leads.discardReason.label': 'Причина отказа',
  'leads.discardReason.confirm': 'Подтвердить',
  'leads.discardReason.loadError': 'Не удалось загрузить причины отказа',
  'leads.rejectionReason': 'Причина отказа',
  'leads.convert.dialogTitle': 'Превратить в сделку',
  'leads.convert.submit': 'Превратить в сделку',
  'leads.convert.error': 'Не удалось конвертировать лид',
  // U-16: статусы и источники лида — i18n (никаких сырых enum в UI).
  'leads.status.NEW': 'Новый',
  'leads.status.IN_PROGRESS': 'В работе',
  'leads.status.CONVERTED': 'Превращён в сделку',
  'leads.status.DISCARDED': 'Отклонён',
  'leads.source.LANDING': 'Лендинг',
  'leads.source.MANUAL': 'Вручную',

  // payments (V12 U-35, волна R66-R67): раздел «Платежи» + блок в карточках.
  'payments.title': 'Платежи',
  'payments.subtitle': 'Учёт платежей и фискальных чеков',
  'payments.loading': 'Загрузка платежей…',
  'payments.loadError': 'Не удалось загрузить платежи',
  'payments.create': 'Создать платёж',
  'payments.create.dialogTitle': 'Создать платёж',
  'payments.create.submit': 'Создать',
  'payments.create.error': 'Не удалось создать платёж',
  'payments.amount': 'Сумма',
  'payments.currency': 'Валюта',
  'payments.paymentDate': 'Дата платежа',
  'payments.fiscalDocNumber': 'Фискальный №',
  'payments.source': 'Источник',
  'payments.status': 'Статус',
  'payments.entity': 'Привязка',
  'payments.entityType.label': 'Тип привязки',
  'payments.actions': 'Действия',
  'payments.rowsPerPage': 'На странице:',
  'payments.of': 'из',
  'payments.empty': 'Нет платежей',
  'payments.emptyDesc': 'Создайте платёж или загрузите чек',
  'payments.upload': 'Загрузить чек',
  'payments.upload.dialogTitle': 'Загрузить чек',
  'payments.upload.submit': 'Загрузить',
  'payments.upload.error': 'Не удалось загрузить чек',
  'payments.upload.file': 'Файл чека',
  'payments.upload.maxSize': 'Файл больше 10 МБ',
  'payments.upload.success': 'Платёж зафиксирован (QR)',
  'payments.upload.createManual': 'Создать вручную',
  'payments.update.error': 'Не удалось изменить статус платежа',
  'payments.confirm': 'Подтвердить',
  'payments.reject': 'Отклонить',
  'payments.block.title': 'Платежи',
  'payments.block.add': '+ Платёж',
  'payments.block.showAll': 'Показать все →',
  'payments.status.PENDING': 'Ожидает',
  'payments.status.CONFIRMED': 'Подтверждён',
  'payments.status.FAILED': 'Отклонён',
  'payments.source.QR': 'QR',
  'payments.source.MANUAL': 'Вручную',
  'payments.entityType.COMPANY': 'Компания',
  'payments.entityType.CONTACT': 'Контакт',
  'payments.entityType.DEAL': 'Сделка',
  'payments.entityType.LEAD': 'Лид',
  // V14 W7 (§3.7/§3.8): счёт (колонка/чип/диалог), поиск по № (NUMBER), сортировка, «Оплатить счёт».
  'payments.invoice': 'Счёт',
  'payments.invoiceNumber': '№ счёта',
  'payments.payForInvoice': 'Оплатить счёт',
  'payments.paymentNumber': '№ платежа',
  'payments.search': 'Поиск по номеру',
  'payments.sort': 'Сортировка',
  'payments.sort.createdAt': 'Дата создания',

  // invoices (V14 W6, §3.8): раздел «Счета» + блок в карточке сделки.
  'invoices.title': 'Счета',
  'invoices.subtitle': 'Выставленные счета по сделкам',
  'invoices.loading': 'Загрузка счетов…',
  'invoices.loadError': 'Не удалось загрузить счета',
  'invoices.empty': 'Нет счетов',
  'invoices.emptyDesc': 'Создайте счёт на сделку',
  'invoices.create': 'Создать счёт',
  'invoices.create.dialogTitle': 'Новый счёт',
  'invoices.create.submit': 'Создать',
  'invoices.create.error': 'Не удалось создать счёт',
  'invoices.create.amountHint': 'Пусто — сумма сделки',
  'invoices.edit.title': 'Редактирование счёта',
  'invoices.edit.save': 'Сохранить',
  'invoices.edit.issue': 'Выставить счёт',
  'invoices.edit.error': 'Не удалось сохранить счёт',
  // V15-a §3.8: срок оплаты можно только изменить (PATCH-контракт: null = «не менять»).
  'invoices.edit.dueDateHint': 'Срок оплаты можно только изменить: пустое значение сохраняет текущий срок',
  'invoices.search': 'Поиск по номеру',
  'invoices.deal': 'Сделка',
  'invoices.amount': 'Сумма',
  'invoices.currency': 'Валюта',
  'invoices.invoiceDate': 'Дата счёта',
  'invoices.dueDate': 'Срок оплаты',
  'invoices.paidAmount': 'Оплачено',
  'invoices.remaining': 'Остаток',
  'invoices.status': 'Статус',
  'invoices.actions': 'Действия',
  'invoices.rowsPerPage': 'На странице:',
  'invoices.of': 'из',
  'invoices.issue': 'Выставить',
  'invoices.void': 'Аннулировать',
  'invoices.pay': 'Оплатить',
  'invoices.editAction': 'Редактировать',
  'invoices.amountInvalid': 'Укажите сумму числом ≥ 0',
  // U-16: статусы счёта (backend InvoiceStatus, конвенция <domain>.<field>.<VALUE>).
  'invoices.status.DRAFT': 'Черновик',
  'invoices.status.OPEN': 'Выставлен',
  'invoices.status.PARTIAL': 'Частично оплачен',
  'invoices.status.PAID': 'Оплачен',
  'invoices.status.OVERDUE': 'Просрочен',
  'invoices.status.VOID': 'Аннулирован',
  'invoices.block.title': 'Счета',
  'invoices.block.add': '+ Счёт',
  'invoices.block.showAll': 'Показать все →',
  'invoices.block.total': 'Итого',
  'invoices.block.paid': 'Оплачено',
  'invoices.block.remaining': 'Остаток',
  'invoices.block.createPayment': 'Создать платёж',

  // reports (V14 W10, ТЗ §3.6): страница «Отчёты» + таблицы R1..R6 + выгрузка XLSX.
  // Стадии/источники/статусы — переиспользуем crm.deals.stage.*, leads.source.*, payments.status.*.
  'reports.title': 'Отчёты',
  'reports.subtitle': 'Аналитика продаж: 7 отчётов с выгрузкой XLSX',
  'reports.loading': 'Загрузка отчёта…',
  'reports.loadError': 'Не удалось загрузить отчёт',
  'reports.empty': 'Нет данных за период',
  'reports.emptyDesc': 'Измените период или фильтры',
  'reports.inactiveDays': 'Без активности, дней',
  'reports.truncated': 'Отчёт обрезан до 5000 строк — используйте фильтры',
  'reports.download.xlsx': 'Выгрузить XLSX',
  'reports.download.error': 'Не удалось выгрузить файл',
  'reports.truncatedXlsx': '⚠ Отчёт обрезан до 5000 строк — используйте фильтры',
  'reports.period.from': 'С даты',
  'reports.period.to': 'По дату',
  'reports.department': 'Департамент',
  'reports.department.all': 'Все департаменты',
  // столбцы таблиц (ReportTable)
  'reports.currency': 'Валюта',
  'reports.count': 'Кол-во',
  'reports.amount': 'Сумма',
  'reports.stage': 'Стадия',
  'reports.dealCount': 'Сделок',
  'reports.leadSource': 'Источник',
  'reports.totalCount': 'Всего лидов',
  'reports.convertedCount': 'Конвертировано',
  'reports.conversionPct': 'Конверсия, %',
  'reports.owner': 'Менеджер',
  'reports.activeAmount': 'Активная сумма',
  'reports.wonCount': 'Выиграно',
  'reports.wonAmount': 'Сумма выигрыша',
  'reports.month': 'Месяц',
  'reports.dealsCreated': 'Сделок создано',
  'reports.dealsCreatedAmount': 'Сумма созданных',
  'reports.paymentsCount': 'Платежей',
  'reports.paymentsAmount': 'Сумма платежей',
  'reports.deal': 'Сделка',
  'reports.nextContact': 'Следующий контакт',
  'reports.lastContact': 'Последний контакт',
  'reports.riskReason': 'Причина риска',
  'reports.sharePct': 'Доля, %',
  'reports.totals': 'Итого',
  'reports.reasonName': 'Причина',
  'reports.leadCount': 'Лидов',
  'reports.total': 'Всего',
  // каталог отчётов (REPORTS titleKey/descriptionKey)
  'reports.report.funnel.title': 'Воронка по стадиям',
  'reports.report.funnel.description': 'Активные сделки по стадиям и валютам (as-of)',
  'reports.report.lead-conversion.title': 'Конверсия лидов',
  'reports.report.lead-conversion.description': 'Конверсия лид → сделка по источникам',
  'reports.report.revenue.title': 'Выручка',
  'reports.report.revenue.description': 'Факт (платежи + закрытые сделки) и прогноз по стадиям',
  'reports.report.revenue.sections.payments': 'Оплачено (факт)',
  'reports.report.revenue.sections.wonDeals': 'Закрыто сделок (факт)',
  'reports.report.revenue.sections.forecast': 'Прогноз',
  'reports.report.by-managers.title': 'Сделки по менеджерам',
  'reports.report.by-managers.description': 'Сделки и суммы в разрезе менеджеров и департаментов',
  'reports.report.monthly-dynamics.title': 'Динамика по месяцам',
  'reports.report.monthly-dynamics.description': 'Созданные сделки, WON и платежи по месяцам',
  'reports.report.risks.title': 'Риски',
  'reports.report.risks.description': 'Без контакта, с просроченным контактом или без активности',
  'reports.report.refusals.title': 'Отказы',
  'reports.report.refusals.description': 'Причины отказа: сделки LOST и лиды DISCARDED за период',
  // причины попадания в «Риски» (RiskReason: NO_NEXT_CONTACT/OVERDUE_NEXT_CONTACT/STALE)
  'reports.riskReason.NO_NEXT_CONTACT': 'Нет запланированного контакта',
  'reports.riskReason.OVERDUE_NEXT_CONTACT': 'Просрочен следующий контакт',
  'reports.riskReason.STALE': 'Нет активности',

  // V15 W7: почта (ТЗ §3.8: аккаунты SMTP/Gmail, шаблоны, история, отправка счёта письмом; U-16).
  // страница «Почта» / вкладки (EmailSettingsPage)
  'email.title': 'Почта',
  'email.subtitle': 'Аккаунты, шаблоны писем и история отправок',
  'email.tabs.accounts': 'Аккаунты',
  'email.tabs.templates': 'Шаблоны',
  'email.tabs.deliveries': 'История',
  // аккаунты (пустое состояние, карточки SMTP/GMAIL)
  'email.empty.title': 'Почта не настроена',
  'email.empty.desc': 'Подключите SMTP-аккаунт или Gmail, чтобы отправлять счета письмами',
  'email.empty.addSmtp': 'Добавить SMTP-аккаунт',
  'email.empty.connectGmail': 'Подключить Gmail',
  'email.accounts.checkConnection': 'Проверить подключение',
  'email.accounts.active': 'Активный',
  'email.accounts.activate': 'Активировать',
  'email.accounts.delete': 'Удалить',
  'email.accounts.smtpTitle': 'SMTP-аккаунт',
  'email.accounts.gmailConnected': 'Gmail подключён',
  'email.accounts.gmailError': 'Ошибка подключения Gmail',
  'email.accounts.disconnectGmail': 'Отключить Gmail',
  'email.accounts.gmailDisconnected': 'Gmail отключён',
  // диалог SMTP (SmtpAccountDialog)
  'email.smtp.title': 'Настройки SMTP',
  'email.smtp.fromEmail': 'Отправитель (email)',
  'email.smtp.displayName': 'Отображаемое имя',
  'email.smtp.host': 'SMTP-хост',
  'email.smtp.port': 'Порт',
  'email.smtp.security': 'Безопасность',
  'email.smtp.security.SSL': 'SSL (465)',
  'email.smtp.security.STARTTLS': 'STARTTLS (587)',
  'email.smtp.username': 'Логин',
  'email.smtp.password': 'Пароль приложения',
  'email.smtp.passwordHint': 'Пароль сохранён; оставьте поле пустым, чтобы не менять',
  'email.smtp.save': 'Сохранить',
  'email.smtp.error': 'Не удалось сохранить SMTP-аккаунт',
  // шаблоны (EmailTemplatesPage + EmailTemplateDialog)
  'email.templates.empty': 'Шаблонов нет',
  'email.templates.emptyDesc': 'Создайте шаблон письма для счетов',
  'email.templates.create': 'Новый шаблон',
  'email.templates.systemBadge': 'Системный',
  'email.templates.loadError': 'Не удалось загрузить шаблоны',
  'email.templates.dialog.title': 'Шаблон письма',
  'email.templates.name': 'Название',
  'email.templates.subject': 'Тема',
  'email.templates.body': 'Тело письма',
  'email.templates.contentType': 'Тип контента',
  'email.templates.contentType.HTML': 'HTML',
  'email.templates.contentType.PLAIN': 'Текст',
  'email.templates.save': 'Сохранить',
  'email.templates.saveError': 'Не удалось сохранить шаблон',
  'email.templates.variables': 'Переменные',
  'email.templates.preview': 'Превью',
  'email.templates.previewError': 'Не удалось сформировать превью',
  'email.templates.invoice': 'Счёт для превью',
  'email.templates.needInvoiceHint': 'Выберите счёт…',
  // история отправок (EmailDeliveriesHistory)
  'email.deliveries.empty': 'Отправок пока нет',
  'email.deliveries.emptyDesc': 'История появится после первой отправки счёта письмом',
  'email.deliveries.loadError': 'Не удалось загрузить историю отправок',
  'email.deliveries.date': 'Дата',
  'email.deliveries.recipient': 'Получатель',
  'email.deliveries.subject': 'Тема',
  'email.deliveries.error': 'Ошибка',
  'email.deliveries.status.SENT': 'Отправлено',
  'email.deliveries.status.FAILED': 'Ошибка',
  'email.deliveries.status.SKIPPED_NO_ACCOUNT': 'Нет аккаунта',
  'email.deliveries.status.SKIPPED_NO_RECIPIENT': 'Нет получателя',
  'email.deliveries.status.SKIPPED_RATE_LIMIT': 'Лимит',
  // диалог «Отправить письмо» (InvoiceEmailDialog + кнопка в InvoiceBlock/InvoicesListPage)
  'email.send.title': 'Отправить письмо',
  'email.send.template': 'Шаблон',
  'email.send.account': 'Аккаунт',
  'email.send.preview': 'Превью',
  'email.send.submit': 'Отправить',
  'email.send.success': 'Письмо отправлено',
  'email.send.failed': 'Письмо не отправлено: {error}',
  'email.send.error': 'Не удалось отправить письмо',
  'email.send.notConfigured': 'Настройте почту, чтобы отправлять счета письмами',
  'email.send.noTemplates': 'Шаблонов писем нет — создайте шаблон в настройках почты',
  'email.send.noRecipientDeal': 'У контакта сделки нет email — укажите email в карточке контакта',
  'email.send.noRecipientLead': 'У лида нет email — укажите email в карточке лида',
  'email.send.recipient': 'Получатель',
  'email.send.to': 'Кому',
  'email.send.toHint': 'Адрес клиента, не SMTP-аккаунт',
  'email.send.subject': 'Тема',
  'email.send.previewRendered': 'Отрендерено',
  'email.send.previewHtml': 'HTML',
  'email.send.recipientMissing': 'Не указан email получателя',
  // навигация (NavRail)
  'email.nav.label': 'Почта',
};
